import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { Search, ShoppingBag, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SearchAutocomplete from '@/components/storefront/SearchAutocomplete';
import AnnouncementBar from '@/components/storefront/AnnouncementBar';
import Logo from '@/components/storefront/Logo';

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { itemCount, openCart } = useCart();
  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/shop', label: 'Products' },
    { to: '/contact', label: 'Need Help?' },
    { to: '/track', label: 'Track Order' },
  ];

  return (
    <>
      <AnnouncementBar />
      <header data-testid="storefront-header" className="sticky top-0 z-40 border-b transition-shadow duration-300"
        style={{ backgroundColor: '#fff', borderColor: 'rgba(0,0,0,0.06)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
          {/* Mobile menu */}
          <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-1.5" aria-label="Menu">
            {mobileOpen ? <X size={22} style={{ color: '#000' }} /> : <Menu size={22} style={{ color: '#000' }} />}
          </button>

          {/* Logo */}
          <Logo variant="dark" />

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-7 absolute left-1/2 -translate-x-1/2">
            {navLinks.map(l => (
              <Link key={l.to} to={l.to} className="font-sans text-[13px] transition-colors duration-200"
                style={{ color: '#333' }}
                onMouseEnter={e => e.target.style.color = '#000'}
                onMouseLeave={e => e.target.style.color = '#333'}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}>
                {l.label}
              </Link>
            ))}
          </nav>

          {/* Right icons */}
          <div className="flex items-center gap-1">
            <SearchAutocomplete />
            <button data-testid="header-cart-btn" onClick={openCart}
              className="relative p-2 rounded-full hover:bg-[rgba(0,0,0,0.04)] transition-colors" aria-label="Cart">
              <ShoppingBag size={19} strokeWidth={1.5} style={{ color: '#000' }} />
              {itemCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-[18px] h-[18px] rounded-full flex items-center justify-center font-sans text-[10px] font-bold"
                  style={{ backgroundColor: '#E8545A', color: '#fff' }}>
                  {itemCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-x-0 top-[88px] z-30 border-b shadow-lg"
            style={{ backgroundColor: '#fff', borderColor: 'rgba(0,0,0,0.06)' }}>
            <nav className="flex flex-col py-4 px-6">
              {navLinks.map(l => (
                <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)}
                  className="py-3 font-sans text-sm border-b"
                  style={{ color: '#333', borderColor: 'rgba(0,0,0,0.04)' }}>
                  {l.label}
                </Link>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
